import React, { ReactNode, useContext } from "react";
import styled, { css } from "styled-components";
import { LayoutGrid, List } from "lucide-react";
import { BotType, LanguageNameSpaces } from "@shared/types";
import SortSelect from "common/Select/SortSelect";
import ExpandCollapse from "common/Animations/ExpandCollapse";
import Panel from "common/NewLook/Panel.tsx";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { MyBotsPageContext } from "@shared/features/bots/MyBotsPage.context";
import { NEW_LOOK_ICON_STROKE } from "common/NewLook/layout";
import {
  getNewLookTokens,
  NEW_LOOK_RADIUS,
  newLookType, getPanelShadow } from "common/NewLook/tokens.ts";
import { NewLookBotsToolbarContext } from "features/bots/NewLook/NewLookBotsToolbar.context";

interface NewLookBotsListHeaderProps {
  isCardsView: boolean;
  setIsCardsView: (isCardsView: boolean) => void;
  count?: number;
  /**
   * Table view: the list itself. The header then becomes the header of the
   * table's panel (title inside the panel); the card grid keeps a section
   * header above the cards.
   */
  children?: ReactNode;
}

/**
 * New look: the bots list header — title and count on the left; search,
 * filters, sorting, the cards/table switch and the page actions in one row
 * of 32px controls on the right. The filters open right underneath.
 */
const NewLookBotsListHeader = ({
  isCardsView,
  setIsCardsView,
  count,
  children,
}: NewLookBotsListHeaderProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.BOTS);
  const {
    botType,
    sortBy,
    setSortBy,
    sortDirection,
    setSortDirection,
    sortOptions,
  } = useContext(MyBotsPageContext)!;
  const toolbar = useContext(NewLookBotsToolbarContext);
  const isPanel = children !== undefined;

  let title = t("tabs.my_bots");
  if (botType === BotType.TRADING_VIEW) title = t("tabs.my_tv_bots");
  else if (botType === BotType.TRADING_VIEW_SIGNALS) {
    title = t("tabs.my_tv_to_signals_bots");
  }

  const layouts = [
    {
      key: "table",
      isCards: false,
      label: t("labels.table_view"),
      icon: List,
    },
    {
      key: "cards",
      isCards: true,
      label: t("labels.cards_view"),
      icon: LayoutGrid,
    },
  ];

  const controls = (
    <Controls>
      {toolbar?.search}
      {toolbar?.filtersButton}
      <SortSelect
        setSortBy={setSortBy}
        sortBy={sortBy}
        sortDirection={sortDirection}
        setSortDirection={setSortDirection}
        sortValues={sortOptions}
        rounded={true}
        labelMapper={(value) => t(value)}
      />
      <LayoutTrack role="radiogroup" aria-label={t("labels.layout")}>
        {layouts.map(({ key, isCards, label, icon: Icon }) => {
          const selected = isCards === isCardsView;
          return (
            <LayoutOption
              key={key}
              type="button"
              role="radio"
              aria-checked={selected}
              aria-label={label}
              title={label}
              $selected={selected}
              onClick={() => !selected && setIsCardsView(isCards)}
            >
              <Icon size={16} strokeWidth={NEW_LOOK_ICON_STROKE} />
            </LayoutOption>
          );
        })}
      </LayoutTrack>
      {toolbar?.pageActions}
    </Controls>
  );

  const filters = toolbar?.filtersChips && (
    <ExpandCollapse expanded={!!toolbar.filtersOpen}>
      {isPanel ? (
        <PanelFilters>{toolbar.filtersChips}</PanelFilters>
      ) : (
        <SectionFilters>{toolbar.filtersChips}</SectionFilters>
      )}
    </ExpandCollapse>
  );

  if (isPanel) {
    return (
      <ListPanel
        title={title}
        count={count}
        actions={controls}
        flush={true}
        data-testid="my-bots-list"
      >
        {filters}
        {children}
      </ListPanel>
    );
  }

  return (
    <>
      <Header>
        <TitleGroup>
          <Title>{title}</Title>
          {count !== undefined && <Count>{count}</Count>}
        </TitleGroup>
        {controls}
      </Header>
      {filters}
    </>
  );
};

export default NewLookBotsListHeader;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px 16px;
  margin-bottom: 12px;
`;
const TitleGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-height: 32px;
`;
const Title = styled.h2`
  margin: 0;
  ${newLookType.panelTitle};
  font-size: 15px;
  color: ${(p) => getNewLookTokens(p.theme).ink};
`;
// The same quiet count as a panel header.
const Count = styled.span`
  ${newLookType.meta};
  font-weight: 500;
  min-width: 20px;
  padding: 1px 6px;
  border-radius: ${NEW_LOOK_RADIUS.chip}px;
  background: ${(p) => getNewLookTokens(p.theme).well};
  box-shadow: inset 0 0 0 1px ${(p) => getNewLookTokens(p.theme).hairline};
  color: ${(p) => getNewLookTokens(p.theme).secondary};
  text-align: center;
`;
const Controls = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 8px;
  margin-left: auto;
  .ant-btn {
    height: 32px;
    padding: 0 14px;
    font-size: 13px;
    gap: 6px;
  }
  .ant-btn-default {
    border: 1px solid ${(p) => getNewLookTokens(p.theme).hairline};
    color: ${(p) => getNewLookTokens(p.theme).ink};
    font-weight: 500;
    box-shadow: none;
    svg {
      color: ${(p) => getNewLookTokens(p.theme).secondary};
    }
  }
`;
// The platform's segmented control: a well track, the selection a white
// thumb.
const LayoutTrack = styled.div`
  display: inline-flex;
  flex: none;
  gap: 2px;
  padding: 2px;
  border-radius: ${NEW_LOOK_RADIUS.control}px;
  background: ${(p) => getNewLookTokens(p.theme).well};
  box-shadow: inset 0 0 0 1px ${(p) => getNewLookTokens(p.theme).hairline};
`;
const LayoutOption = styled.button<{ $selected: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 28px;
  padding: 0;
  border: none;
  border-radius: ${NEW_LOOK_RADIUS.control - 2}px;
  background: transparent;
  color: ${(p) => getNewLookTokens(p.theme).muted};
  cursor: pointer;
  transition: color 120ms, background-color 120ms, box-shadow 120ms;
  &:hover {
    color: ${(p) => getNewLookTokens(p.theme).ink};
  }
  &:focus-visible {
    outline: 2px solid ${(p) => getNewLookTokens(p.theme).focus};
    outline-offset: 1px;
  }
  ${(p) =>
    p.$selected &&
    css`
      color: ${getNewLookTokens(p.theme).ink};
      background: ${getNewLookTokens(p.theme).panel};
      box-shadow: 0 1px 2px rgba(13, 20, 78, 0.08),
        inset 0 0 0 1px ${getNewLookTokens(p.theme).hairline};
      cursor: default;
    `}
`;
// Above the card grid: the filters on their own panel.
const SectionFilters = styled.div`
  margin-bottom: 16px;
  padding: 16px 20px;
  background: ${(p) => getNewLookTokens(p.theme).panel};
  border-radius: ${NEW_LOOK_RADIUS.panel}px;
  box-shadow: ${(p) => getPanelShadow(p.theme)};
`;
// Inside the table's panel: a band under its header, over the rows.
const PanelFilters = styled.div`
  padding: 16px 20px;
  background: ${(p) => getNewLookTokens(p.theme).well};
  border-bottom: 1px solid ${(p) => getNewLookTokens(p.theme).hairline};
`;
const ListPanel = styled(Panel)`
  > header {
    flex-wrap: wrap;
    row-gap: 8px;
  }
`;
